import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { hoistStatics } from 'recompose';
import styled from 'styled-components';

import HTMLView from 'Components/HTMLView';

import { ButtonFilled as Button, Flex, Text, Box, SvgIcon, ButtonOutlined } from '../Base';

import { QuestionBlock, EXAM_TYPE, EXAM_MODE } from './Components/Exam';

const ViewSolutionButton = styled(ButtonOutlined)`
  background-color: transparent;
  &:disabled {
    color: #a6a6a6;
    background-color: transparent;
    border-color: #d9d9d9;
    cursor: not-allowed;
  }
`;

const SubmitAnswerButton = styled(Button)`
  &:disabled {
    color: #a6a6a6;
    background-color: #eaeaea;
    border-color: #eaeaea;
    cursor: not-allowed;
  }
`;

const HelperBoxView = ({ isSolution = false, isFillInTheBlankQuiz, children }) => (
  <Box
    mt={4}
    px={40}
    py={32}
    bg={isSolution ? 'rgba(0,197,88,0.1)' : 'rgba(251, 78, 78,0.1)'}
    style={{
      borderRadius: 12,
    }}
  >
    <Box>
      {isFillInTheBlankQuiz && (
        <Flex justifyContent="center" pt={4} pb={5}>
          <SvgIcon icon={isSolution ? SvgIcon.ICON_LIST.CORRECT : SvgIcon.ICON_LIST.INCORRECT} />
        </Flex>
      )}
      {children}
    </Box>
  </Box>
);

const Footer = ({ t, onSubmitAnswer, disabledViewSolution, disabledSubmit, onViewSolution }) => {
  return (
    <Flex flexDirection="row" justifyContent="flex-end" mt={6}>
      <ViewSolutionButton
        minWidth={['100px', '125px', '150px']}
        borderRadius="100px"
        mr={2}
        onClick={onViewSolution}
        disabled={disabledViewSolution}
      >
        {t('BUTTON.VIEW_SOLUTION')}
      </ViewSolutionButton>
      <SubmitAnswerButton
        minWidth={['100px', '125px', '150px']}
        borderRadius="100px"
        onClick={onSubmitAnswer}
        disabled={disabledSubmit}
      >
        {t('BUTTON.SUBMIT')}
      </SubmitAnswerButton>
    </Flex>
  );
};

const getAnsweredHint = (question, answeredQuestions, isFillInTheBlankQuiz) => {
  if (isFillInTheBlankQuiz) {
    return question.fillInTheBlankItems[0]?.hint;
  }
  return question.choices.find((choice) => choice.id === answeredQuestions[0])?.hint;
};

// FIXME: Hint block cannot support multiple answers

const HintBlock = ({ t, question, answeredQuestions, isFillInTheBlankQuiz }) => {
  if (question.type === EXAM_TYPE.MULTIPLE_SELECTION) {
    const { isAnswerAllCorrectChoice, isCorrect } = checkCompleteness(question, answeredQuestions);
    if (!isCorrect) {
      return (
        <HelperBoxView isFillInTheBlankQuiz={isFillInTheBlankQuiz}>
          <Text>
            {isAnswerAllCorrectChoice ? t('STATUS.WRONG_ANSWER') : t('STATUS.MISSING_ANSWERS')}
          </Text>
        </HelperBoxView>
      );
    }
  }

  const answeredHint = getAnsweredHint(question, answeredQuestions, isFillInTheBlankQuiz);
  if (!answeredHint) {
    return null;
  }

  return (
    <HelperBoxView isFillInTheBlankQuiz={isFillInTheBlankQuiz}>
      <Text textStyle={'caption'} fontWeight={'normal'} pb={2} fontFamily="content" color="hint">
        {t('TITLE.TIPS')}
      </Text>
      <Text>
        <HTMLView rawContent={answeredHint} />
      </Text>
    </HelperBoxView>
  );
};

const SolutionBlock = ({ t, question, isFillInTheBlankQuiz }) => {
  let answer;
  let solution;
  answer = question?.fillInTheBlankItems?.[0].answer;
  solution = question?.solution;
  if (!answer && !solution) {
    return null;
  }
  return (
    <HelperBoxView isSolution isFillInTheBlankQuiz={isFillInTheBlankQuiz}>
      {answer && (
        <>
          <Text
            textStyle={'caption'}
            pb={2}
            fontWeight={'normal'}
            fontFamily="content"
            color="hint"
          >
            {t('TITLE.SOLUTION')}
          </Text>
          <Text>{answer}</Text>
        </>
      )}
      {solution && (
        <>
          <Text
            textStyle={'caption'}
            pt={4}
            fontWeight={'normal'}
            fontFamily="content"
            color="hint"
          >
            {t('TITLE.DESCRIPTION')}
          </Text>
          <Text>
            <HTMLView rawContent={solution} />
          </Text>
        </>
      )}
    </HelperBoxView>
  );
};

const mappingQuestionData = (
  mode,
  originalQuestionData = {},
  answeredQuestions = [],
  isCorrect = null
) => {
  const result = { ...originalQuestionData };
  result.choices = originalQuestionData.choices?.map((choice) => ({
    ...choice,
    isCorrect: mode === EXAM_MODE.VIEW_SOLUTION ? choice.isCorrect : null,
    isSelected: answeredQuestions.includes(choice.id),
  }));
  result.isCorrect = isCorrect;
  return result;
};

const checkCompleteness = (question, answeredQuestions) => {
  const choices = question.choices;

  // Correct Definition: All Answered Questions === All isAnswer choices;
  const correctChoiceIds = choices.filter((c) => c.isAnswer).map((c) => c.id.toString());
  const isAnswerAllCorrectChoice = correctChoiceIds.every((choiceId) =>
    answeredQuestions.includes(choiceId)
  );
  const isAllCorrectChoiceAreAnswers = answeredQuestions.every((answerId) =>
    correctChoiceIds.includes(answerId)
  );

  const isCorrect = isAnswerAllCorrectChoice && isAllCorrectChoiceAreAnswers;
  return { isAnswerAllCorrectChoice, isCorrect };
};

const checkAnswers = (questionData = {}, answeredQuestions) => {
  if (!questionData) return null;
  const { type } = questionData;
  if (type === EXAM_TYPE.FILL_IN_THE_BLANK) {
    const gradeAnswer = questionData.fillInTheBlankItems[0]?.gradeAnswer;
    if (gradeAnswer) {
      const correctAnswer = questionData.fillInTheBlankItems[0]?.answer;
      const userAnswer = answeredQuestions;
      if (typeof correctAnswer === 'string' && typeof userAnswer === 'string') {
        return correctAnswer.trim().toLowerCase() === userAnswer.trim().toLowerCase();
      }
      return false;
    }
    return true;
  }
  const { isCorrect } = checkCompleteness(questionData, answeredQuestions);

  return isCorrect;
};

const getAnsweredChoiceIndex = (questionData = {}, answeredQuestions = []) => {
  let answeredChoiceIndexList = [];
  const choices = questionData.choices;
  answeredQuestions.forEach(
    (answer) =>
      (answeredChoiceIndexList = [
        ...answeredChoiceIndexList,
        choices.findIndex((c) => c.id === answer),
      ])
  );
  return answeredChoiceIndexList;
};

class QuizSimple extends Component {
  static propTypes = {
    question: PropTypes.object.isRequired,
    currentQuestionNumber: PropTypes.number,
    onAnswerQuiz: PropTypes.func,
    userAssetDocument: PropTypes.shape({
      userAnswers: PropTypes.arrayOf(
        PropTypes.shape({ id: PropTypes.string, value: PropTypes.string })
      ),
    }),
    onUpdateUserAssetData: PropTypes.func,
  };

  static defaultProps = {
    onAnswerQuiz: () => {},
  };

  state = {
    quizMode: EXAM_MODE.ANSWERING,
    answeredQuestions: [],
    isCorrect: null,
    isShowSolution: null,
  };

  componentDidMount() {
    const targetUserAnswer = this.props.userAssetDocument?.userAnswers.find(
      (userAnswer) => userAnswer.id === this.props.question.id
    );
    if (!targetUserAnswer) {
      return;
    }
    this.setState({
      answeredQuestions: targetUserAnswer.value,
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.userAssetDocument === this.props.userAssetDocument) return;

    const targetUserAnswer = this.props.userAssetDocument?.userAnswers.find(
      (userAnswer) => userAnswer.id === this.props.question.id
    );
    if (!targetUserAnswer) {
      return;
    }
    this.setState({
      answeredQuestions: targetUserAnswer.value,
    });
  }

  handleSubmitAnswer = () => {
    const { question, onAnswerQuiz, userAssetDocument, onUpdateUserAssetData } = this.props;
    const { answeredQuestions } = this.state;
    const isCorrect = checkAnswers(question, answeredQuestions);
    const { type } = question;
    const isFillInTheBlankQuiz = type === EXAM_TYPE.FILL_IN_THE_BLANK;

    // In current, quiz simple support save user asset only fill in the blank type.
    if (isCorrect && isFillInTheBlankQuiz) {
      const newUserAnswers = userAssetDocument?.userAnswers ?? [];
      const currentUserAnswerIndex = newUserAnswers.findIndex(
        (userAnswer) => userAnswer.id === question.id
      );
      if (currentUserAnswerIndex === -1) {
        newUserAnswers.push({ id: question.id, value: answeredQuestions });
      } else {
        newUserAnswers[currentUserAnswerIndex] = { id: question.id, value: answeredQuestions };
      }
      onUpdateUserAssetData({ userAnswers: newUserAnswers });
    }

    const answeredChoiceIndex =
      // TODO: FILL_IN_THE_BLANK is not support tracking user’s answers
      !isFillInTheBlankQuiz && getAnsweredChoiceIndex(question, answeredQuestions);

    this.setState(
      {
        quizMode: EXAM_MODE.VIEW_SOLUTION,
        isCorrect,
      },
      () => {
        onAnswerQuiz(isCorrect, answeredChoiceIndex, isFillInTheBlankQuiz);
      }
    );
  };

  handleAnswerQuestion = (answeredQuestions) => {
    this.setState({ quizMode: EXAM_MODE.ANSWERING, answeredQuestions: answeredQuestions });
  };

  handleRetryAnswer = () => {
    this.setState({ quizMode: EXAM_MODE.ANSWERING, answeredQuestions: [], isCorrect: null });
  };

  handleViewSolution = () => {
    this.setState({ quizMode: EXAM_MODE.VIEW_SOLUTION });
  };

  handleViewSolutionByClickViewSolution = () => {
    const { answeredQuestions, isCorrect } = this.state;
    const { question, onAnswerQuiz } = this.props;
    const isFillInTheBlankQuiz = question.type === EXAM_TYPE.FILL_IN_THE_BLANK;
    let correctAnswers;
    if (isFillInTheBlankQuiz) {
      correctAnswers = question?.fillInTheBlankItems[0].answer;
    } else {
      correctAnswers = question?.choices.filter(({ isAnswer }) => isAnswer).map(({ id }) => id);
    }
    const answeredChoiceIndex =
      !isFillInTheBlankQuiz && getAnsweredChoiceIndex(question, correctAnswers);

    this.setState(
      {
        quizMode: EXAM_MODE.VIEW_SOLUTION,
        isCorrect: isCorrect,
        isShowSolution: true,
        answeredQuestions,
      },
      () => {
        // call API that the user answered correctly
        onAnswerQuiz(true, answeredChoiceIndex, isFillInTheBlankQuiz);
      }
    );
  };

  renderHelperBlock = (isFillInTheBlankQuiz) => {
    const { t, question } = this.props;
    const { quizMode, isCorrect, answeredQuestions, isShowSolution } = this.state;
    const gradeAnswer = question.fillInTheBlankItems?.[0]?.gradeAnswer ?? true;
    if (quizMode === EXAM_MODE.VIEW_SOLUTION) {
      if (isShowSolution || isCorrect || !gradeAnswer) {
        return (
          <SolutionBlock t={t} question={question} isFillInTheBlankQuiz={isFillInTheBlankQuiz} />
        );
      }
      return (
        <HintBlock
          t={t}
          question={question}
          answeredQuestions={answeredQuestions}
          isFillInTheBlankQuiz={isFillInTheBlankQuiz}
        />
      );
    }
    return;
  };

  renderFooterBlock = () => {
    const { t, question } = this.props;
    const { quizMode, answeredQuestions, isCorrect, isShowSolution } = this.state;
    const gradeAnswer = question.fillInTheBlankItems?.[0]?.gradeAnswer ?? true;

    return (
      <Footer
        t={t}
        quizMode={quizMode}
        isCorrect={isCorrect}
        disabledSubmit={
          (quizMode === EXAM_MODE.ANSWERING && answeredQuestions.length === 0) ||
          isCorrect ||
          (quizMode === EXAM_MODE.VIEW_SOLUTION && (gradeAnswer || isShowSolution))
        }
        disabledViewSolution={isShowSolution || isCorrect}
        onSubmitAnswer={this.handleSubmitAnswer}
        onRetry={this.handleRetryAnswer}
        onViewSolution={this.handleViewSolutionByClickViewSolution}
      />
    );
  };

  render() {
    const { question, currentQuestionNumber } = this.props;
    const { quizMode, answeredQuestions, isCorrect, isShowSolution } = this.state;
    const { type } = question;
    const isFillInTheBlankQuiz = type === EXAM_TYPE.FILL_IN_THE_BLANK;
    return (
      <Fragment>
        <QuestionBlock
          question={mappingQuestionData(quizMode, question, answeredQuestions, isCorrect)}
          mode={quizMode}
          onAnswer={this.handleAnswerQuestion}
          currentQuestionNumber={currentQuestionNumber}
          isFillInTheBlankQuiz={isFillInTheBlankQuiz}
          isCorrect={isCorrect}
          isShowSolution={isShowSolution}
          renderFooterBlock={this.renderFooterBlock}
          userAnswer={answeredQuestions}
        />
        {this.renderHelperBlock(isFillInTheBlankQuiz)}
        {!isFillInTheBlankQuiz && this.renderFooterBlock()}
      </Fragment>
    );
  }
}

QuizSimple.EXAM_TYPE = EXAM_TYPE;

export default hoistStatics(withTranslation('quizSimple'))(QuizSimple);
